/**
 * ErrorBoundary.tsx — two complementary safety nets for render-time failures.
 *
 * The app previously had NO error handling of any kind: an uncaught render
 * throw (in a provider, a page, or app chrome) or a failed `lazy()` chunk
 * import would unmount the whole React tree and leave a blank white screen.
 * This module fixes that with two exports that share one presentational
 * fallback (`FallbackUI`, styled to match the design system):
 *
 *  1. `ErrorBoundary` — a class component (React error boundaries MUST be
 *     classes; hooks cannot implement `componentDidCatch`). It wraps the app
 *     root in `main.tsx`, ABOVE every provider and the RouterProvider, so a
 *     throw inside ThemeProvider / AuthProvider / BrandingProvider / the router
 *     itself still renders a recoverable UI instead of a blank page. It uses
 *     only the Button primitive and a hard reload (no router hooks), so it
 *     stays renderable even if a provider above the router is what threw. It
 *     resets its captured error via a "Try again" action and whenever any
 *     value in `resetKeys` changes.
 *
 *  2. `RouteErrorBoundary` — a function component used as a route
 *     `errorElement`. React Router routes the nearest matched route's error
 *     here via `useRouteError()`, which covers (a) render throws inside a page
 *     or Layout subtree, and (b) `lazy()` dynamic-import failures (a stale
 *     chunk after a redeploy). It detects the stale-chunk case from the
 *     browser's dynamic-import error strings and steers the user to a reload;
 *     it renders 4xx/5xx route responses sensibly; and it falls back to the
 *     shared UI for generic errors. "Try again" uses a hard reload because
 *     React Router does not clear a captured route error on same-route
 *     navigation; "Go to home" is a normal link, which re-matches routes and
 *     clears the boundary.
 *
 * Technical error/stack details are shown ONLY in development (`env.isDev`).
 */
import * as React from "react";
import { Link, isRouteErrorResponse, useRouteError } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Home,
  RotateCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { env } from "@/lib/env";
import { captureException } from "@/lib/observability";

/* ──────────────────────────────────────────────────────────────────────────
 * Stale-chunk detection
 *
 * When a `lazy()`-loaded chunk can no longer be fetched (e.g. the file was
 * renamed/removed by a redeploy), the browser's native dynamic `import()`
 * rejects with a TypeError whose message is one of the three strings below —
 * one per engine (Chromium / Firefox / Safari). React.lazy forwards that
 * rejection to the nearest error boundary. We match those exact strings (not a
 * generic "failed to fetch", which would also swallow ordinary API/network
 * errors) so we only recommend a reload when a reload would actually help —
 * it re-reads the current chunk manifest.
 * ──────────────────────────────────────────────────────────────────────── */
const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chromium / Edge
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
];

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name =
    error instanceof Error ? error.name : (error as { name?: string }).name;
  if (name === "ChunkLoadError") return true;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : (error as { message?: string }).message ?? "";
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

function errorToParts(error: unknown): {
  name: string;
  message: string;
  stack?: string;
} {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  if (typeof error === "string") {
    return { name: "Error", message: error };
  }
  try {
    return { name: "Error", message: JSON.stringify(error) };
  } catch {
    return { name: "Error", message: String(error) };
  }
}

function reloadPage() {
  if (typeof window !== "undefined") window.location.reload();
}

/* ──────────────────────────────────────────────────────────────────────────
 * Shared presentational fallback (design-system styled, no Card primitive —
 * mirrors NotFound.tsx: centered plain wrapper, danger-toned icon ring).
 * Announced to assistive tech via role="alert" and takes focus on mount.
 * ──────────────────────────────────────────────────────────────────────── */
interface FallbackUIProps {
  /** Big heading. */
  title?: string;
  /** Muted explanatory copy. */
  description?: React.ReactNode;
  /** Primary action (left button). */
  primaryAction?: React.ReactNode;
  /** Secondary action (right button). */
  secondaryAction?: React.ReactNode;
  /** The captured error — its details are shown only in development. */
  error?: unknown;
  /** When false, the page is not stretched to full height. */
  fullHeight?: boolean;
}

function FallbackUI({
  title = "Something went wrong",
  description = "An unexpected error occurred. You can try again below, or reload the page.",
  primaryAction,
  secondaryAction,
  error,
  fullHeight = true,
}: FallbackUIProps) {
  const [showDetails, setShowDetails] = React.useState(false);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const parts = error != null ? errorToParts(error) : null;
  const showTech = env.isDev && parts != null;

  // Move focus to the heading so keyboard / screen-reader users land on the
  // error message instead of staying on a now-unmounted control.
  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      role="alert"
      className={cn(
        "grid place-items-center bg-bg p-6",
        fullHeight ? "min-h-svh" : "min-h-[60vh]",
      )}
    >
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-2xl bg-primary/10 text-primary">
          <AlertTriangle className="h-8 w-8" />
        </div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl font-semibold tracking-tight text-fg outline-none"
        >
          {title}
        </h1>
        <p className="mt-2 text-sm text-fg-muted">{description}</p>

        {(primaryAction || secondaryAction) && (
          <div className="mt-6 flex items-center justify-center gap-3">
            {primaryAction}
            {secondaryAction}
          </div>
        )}

        {showTech && (
          <div className="mt-4">
            <button
              type="button"
              aria-expanded={showDetails}
              onClick={() => setShowDetails((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
            >
              {showDetails ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {showDetails ? "Hide error details" : "Show error details"}
            </button>

            {showDetails && (
              <div className="mt-2 rounded-md border border-border bg-bg-subtle p-3 text-left">
                <div className="font-mono text-xs text-fg-muted">
                  {parts!.name}
                </div>
                <div className="mt-1 wrap-break-word font-mono text-xs text-danger">
                  {parts!.message}
                </div>
                {parts!.stack && (
                  <pre className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap font-mono text-[10px] text-fg-muted">
                    {parts!.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * ErrorBoundary — class component for the app root (main.tsx).
 *
 * Catches render throws from EVERY descendant (all providers + the router).
 * Intentionally self-contained: its fallback uses only the Button primitive
 * and a hard reload (no router hooks), so it stays renderable even when a
 * provider ABOVE the router is the thing that threw.
 * ──────────────────────────────────────────────────────────────────────── */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Optional custom fallback. Receives the captured error and a `reset`
   * callback that clears the boundary's error state. If omitted, the shared
   * FallbackUI is rendered.
   */
  fallback?: (args: { error: unknown; reset: () => void }) => React.ReactNode;
  /**
   * When any value in this array changes (shallow compare), the boundary
   * clears its error state and re-renders children — useful for resetting on
   * navigation or auth changes.
   */
  resetKeys?: ReadonlyArray<unknown>;
}

interface ErrorBoundaryState {
  error: unknown;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // Surface to the console so it's still visible in dev tools / log capture.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
    // A white screen on a vessel's tablet used to produce no signal at all
    // beyond a support email. No-op unless VITE_SENTRY_DSN is configured.
    captureException(error, { componentStack: info.componentStack });
  }

  // Recover on browser navigation. Without this, once the boundary catches an
  // error (e.g. a lazy-route chunk that failed to load offline, or any transient
  // render throw) the WHOLE app stays on the fallback until a hard reload — so
  // pressing Back appeared to "not load any data until the app is restarted".
  // popstate fires for the hardware/in-app Back button and navigate(-1); on it
  // we clear the error so React re-renders the (now-different) route and retries.
  private onPopState = () => { if (this.state.error != null) this.reset(); };

  componentDidMount() {
    window.addEventListener("popstate", this.onPopState);
  }

  componentWillUnmount() {
    window.removeEventListener("popstate", this.onPopState);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error == null) return;
    const prev = prevProps.resetKeys;
    const next = this.props.resetKeys;
    if (!prev || !next) return;
    const changed =
      prev.length !== next.length ||
      next.some((k, i) => !Object.is(k, prev[i]));
    if (changed) this.reset();
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error == null) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    const chunk = isChunkLoadError(error);
    return (
      <FallbackUI
        error={error}
        title={chunk ? "Update available" : "Something went wrong"}
        description={
          chunk
            ? "A newer version of the app is available. Please reload to continue."
            : "An unexpected error occurred. You can try again, or reload the page."
        }
        primaryAction={
          <Button onClick={reloadPage}>
            <RotateCw className="mr-2 h-4 w-4" /> Reload page
          </Button>
        }
        secondaryAction={
          chunk ? undefined : (
            <Button variant="outline" onClick={this.reset}>
              Try again
            </Button>
          )
        }
      />
    );
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * RouteErrorBoundary — function component for route `errorElement`s.
 *
 * Reads the error React Router captured for the nearest matching route and
 * handles three cases: stale-chunk dynamic-import failures, HTTP-style route
 * responses (isRouteErrorResponse), and generic render throws. "Try again"
 * does a hard reload (React Router keeps a captured route error across
 * same-route navigation, so navigate(0) would not clear it); "Go to home" is a
 * link to a different route, which re-matches and clears the boundary.
 * ──────────────────────────────────────────────────────────────────────── */
function RouteErrorBoundary() {
  const error = useRouteError();

  const goHome = (
    <Button variant="outline" asChild>
      <Link to="/">
        <Home className="mr-2 h-4 w-4" /> Go to home
      </Link>
    </Button>
  );

  const tryAgain = (
    <Button onClick={reloadPage}>
      <RotateCw className="mr-2 h-4 w-4" /> Try again
    </Button>
  );

  // Defensive: useRouteError can be null if something rendered this without a
  // captured error. Show a generic fallback rather than dereferencing null.
  if (error == null) {
    return (
      <FallbackUI
        title="Something went wrong"
        description="An unexpected error occurred. You can reload the page, or head back home."
        primaryAction={<Button onClick={reloadPage}><RotateCw className="mr-2 h-4 w-4" /> Reload page</Button>}
        secondaryAction={goHome}
      />
    );
  }

  // 1. Stale chunk: a redeploy invalidated the lazy() chunk this route imports.
  //    A reload re-reads the current chunk manifest, so make that the sole CTA.
  if (isChunkLoadError(error)) {
    return (
      <FallbackUI
        error={error}
        title="Update available"
        description="A newer version of the app is available. Please reload the page to load the latest version."
        primaryAction={
          <Button onClick={reloadPage}>
            <RotateCw className="mr-2 h-4 w-4" /> Reload page
          </Button>
        }
      />
    );
  }

  // 2. HTTP-style route error response (thrown Response / 4xx / 5xx).
  if (isRouteErrorResponse(error)) {
    const is404 = error.status === 404;
    return (
      <FallbackUI
        error={error}
        title={is404 ? "Page not found" : `${error.status} ${error.statusText}`}
        description={
          is404
            ? "We couldn't find the page you're looking for. It may have been moved, or the link could be out of date."
            : "The server returned an error while loading this page. You can try again, or head back home."
        }
        primaryAction={tryAgain}
        secondaryAction={goHome}
      />
    );
  }

  // 3. Generic render error within a page / Layout subtree.
  return (
    <FallbackUI
      error={error}
      title="Something went wrong"
      description="An unexpected error occurred while loading this page. You can try again, or head back home."
      primaryAction={tryAgain}
      secondaryAction={goHome}
    />
  );
}

export { ErrorBoundary, RouteErrorBoundary, FallbackUI };
